/* eslint-disable react/no-unstable-nested-components */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/styles';
import ListItemIcon from '@mui/material/ListItemIcon';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Collapse from '@mui/material/Collapse';
import Avatar from '@mui/material/Avatar';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import useSettings from '../../hooks/useSettings';
import { LANGUAGE } from '../../constants/Select';
import {
	getSupportedLocaleOptions,
	resolveSupportedLocale,
} from '../../locales/localeConfig';

const flagSources = getSupportedLocaleOptions().reduce((sources, { flag }) => {
	const flagCode = (flag || '').toLowerCase();

	if (!flagCode) {
		return sources;
	}

	// eslint-disable-next-line global-require, import/no-dynamic-require
	const source = require(`../../assets/flags/${flagCode.toUpperCase()}.png`);

	return { ...sources, [flagCode]: source.default || source };
}, {});

function FlagIcon({ flagCode, alt }) {
	const source = flagSources[(flagCode || '').toLowerCase()];

	if (!source) {
		return null;
	}

	return <Avatar alt={alt} src={source} sx={{ width: 24, height: 24 }} />;
}

export default function LanguageSelector({
	placeholder,
	labels,
	withBorder = false,
}) {
	const theme = useTheme();
	const [open, setOpen] = useState(false);
	const { settings, saveSettings } = useSettings();
	const resolvedLabels = labels?.length ? labels : LANGUAGE;

	const options = useMemo(() => {
		if (resolvedLabels?.length) {
			return resolvedLabels.map((item) => {
				const locale = resolveSupportedLocale(item.value);
				const fallback =
					getSupportedLocaleOptions().find((opt) => opt.locale === locale) ||
					{};

				return {
					locale,
					nativeLabel: item.label || fallback.nativeLabel,
					flag: fallback.flag,
				};
			});
		}

		return getSupportedLocaleOptions();
	}, [resolvedLabels]);

	const selectedLocale = resolveSupportedLocale(
		settings?.locale || settings?.language
	);

	const selectedIndex = Math.max(
		0,
		options.findIndex(({ locale }) => locale === selectedLocale)
	);

	const handleChange = () => {
		setOpen(!open);
	};

	const onSelect = (locale) => {
		saveSettings({ locale: resolveSupportedLocale(locale) });
		setOpen(false);
	};

	const getLocaleLabel = (option) => {
		if (!option) {
			return '';
		}

		return option.nativeLabel || option.locale;
	};

	const getListItemContent = (locale) => {
		const selectedOption =
			options.find(
				(option) => option.locale === resolveSupportedLocale(locale)
			) || options[0];
		const localeLabel = getLocaleLabel(selectedOption);

		return (
			<>
				{selectedOption?.flag && (
					<ListItemIcon>
						<FlagIcon flagCode={selectedOption.flag} alt={localeLabel} />
					</ListItemIcon>
				)}
				<ListItemText
					primary={localeLabel}
					primaryTypographyProps={{
						fontSize: theme.palette.textSize.sizeMd,
						noWrap: true,
					}}
					sx={{
						minWidth: 0,
						'& .MuiTypography-root': {
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						},
					}}
				/>
			</>
		);
	};

	const renderHeader = () => {
		if (open) {
			return (
				<ListItemText
					primary={placeholder}
					primaryTypographyProps={{
						color: theme.palette.body.main,
						fontSize: theme.palette.textSize.sizeMd,
						noWrap: true,
					}}
					sx={{
						minWidth: 0,
						'& .MuiTypography-root': {
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						},
					}}
				/>
			);
		}
		return getListItemContent(selectedLocale);
	};

	const selector = (
		<div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
			<List
				component="nav"
				aria-labelledby="nested-list-subheader"
				sx={
					withBorder
						? {
								width: '100%',
								maxWidth: '100%',
								boxSizing: 'border-box',
								py: 0,
								border: `1px solid ${theme.palette.base.border}`,
								borderColor: open
									? theme.palette.primary.main
									: theme.palette.base.border,
								borderRadius: theme.palette.borderRadius.Md,
								overflow: 'hidden',
								transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
								boxShadow: open
									? `inset 0 0 0 1px ${theme.palette.primary.main}`
									: 'none',
								'&:focus-within': {
									borderColor: theme.palette.primary.main,
									boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
								},
						  }
						: {
								width: '100%',
								maxWidth: '100%',
								boxSizing: 'border-box',
								py: 0,
						  }
				}
			>
				<ListItem
					onClick={handleChange}
					sx={{
						paddingLeft: theme.spacing(3),
						py: theme.spacing(0.5),
						minHeight: theme.spacing(4.5),
						minWidth: 0,
					}}
				>
					{renderHeader()}
					{open ? <ExpandLess /> : <ExpandMore />}
				</ListItem>

				<Collapse in={open} timeout="auto" unmountOnExit>
					<List component="div" disablePadding>
						{options.map((item, i) => (
							<React.Fragment key={item.locale}>
								<ListItem
									value={item.locale}
									onClick={() => {
										onSelect(item.locale);
									}}
									selected={selectedIndex === i}
									sx={{
										'&:hover': {
											backgroundColor: theme.palette.action.hover,
										},
										paddingLeft: theme.spacing(3),
										minWidth: 0,
									}}
									data-testid={`${item.locale}-item`}
									data-testvalue={item.locale}
								>
									{getListItemContent(item.locale)}
								</ListItem>
							</React.Fragment>
						))}
					</List>
				</Collapse>
			</List>
		</div>
	);

	return selector;
}
LanguageSelector.propTypes = {
	placeholder: PropTypes.string,
	labels: PropTypes.array,
	withBorder: PropTypes.bool,
};

FlagIcon.propTypes = {
	flagCode: PropTypes.string,
	alt: PropTypes.string,
};
